import { useEffect, useRef, useState } from "react";
import {
    RefreshCw,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    ChevronDown,
    History,
    Play,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtimeChannel } from "@/context/RealtimeContext";

/**
 * Live sync badge shown in each Board's action strip.
 *   - Collapsed: "SYNC 3m ago · Monday.com" with a chevron.
 *   - Expanded: dropdown panel with full last-synced timestamp, the last 5 tick
 *     results (status, delta counts, message), and — for admins — a "Run sync
 *     now" button.
 */
export default function SyncStatusBadge({ target }) {
    const { user } = useAuth();
    const isAdmin = (user?.roles || []).includes("admin");
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    const [tick, setTick] = useState(0);
    const rootRef = useRef(null);

    const loadStatus = async () => {
        try {
            const { data } = await api.get(`/migrate/sync-status?target=${target}`);
            setStatus(data);
        } catch (_e) {
            setStatus(null);
        }
    };

    const loadHistory = async () => {
        try {
            const { data } = await api.get(`/migrate/sync-history?target=${target}&limit=5`);
            setHistory(data.runs || []);
        } catch (_e) {
            setHistory([]);
        }
    };

    useEffect(() => {
        loadStatus();
        loadHistory();
        const id = setInterval(() => setTick((v) => v + 1), 30000);
        const id2 = setInterval(loadStatus, 60000);
        return () => {
            clearInterval(id);
            clearInterval(id2);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);

    useRealtimeChannel("boards", () => {
        loadStatus();
        loadHistory();
    });

    // Refresh history whenever the dropdown opens (cheap, keeps it fresh).
    useEffect(() => {
        if (open) loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Outside-click closes the dropdown.
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    if (!status?.enabled) return null;

    const err = status.last_sync_status === "error";
    const age = ageLabel(status.last_sync_at, tick);
    const label = err
        ? `SYNC ERROR · ${age || "never"}`
        : status.last_sync_at
        ? `SYNC ${age} · ${status.source || "monday"}`
        : `SYNC PENDING`;

    const color = err
        ? "text-virtus-stuck border-virtus-stuck"
        : "text-virtus-orange border-virtus-orange/50";

    const runNow = async () => {
        if (!isAdmin || busy) return;
        setBusy(true);
        try {
            const { data } = await api.get("/migrate/sync-configs");
            const cfg = (data.configs || []).find((c) => c.target === target && c.enabled);
            if (!cfg) {
                toast.error("No active sync config for this board.");
                return;
            }
            const { data: r } = await api.post(`/migrate/sync-configs/${cfg.id}/run`);
            if (r.result?.last_sync_status === "ok") {
                const cs = r.result?.last_sync_counts || {};
                toast.success(
                    `Sync complete · ${cs.updated_items || 0} updated, ${cs.inserted_items || 0} added, ${cs.deleted_items || 0} deleted`
                );
            } else {
                toast.error(r.result?.last_sync_message || "Sync failed");
            }
            await loadStatus();
            await loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div ref={rootRef} className="relative" data-testid="sync-status-wrapper">
            <button
                onClick={() => setOpen((v) => !v)}
                title={
                    err
                        ? status.last_sync_message || "Last sync failed"
                        : `${status.board_name || "Monday board"} · polls every ${status.interval_minutes || 5} min`
                }
                className={`flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border bg-[#0A0A0A] ${color} hover:bg-virtus-orange/10 cursor-pointer transition-colors`}
                data-testid="sync-status-badge"
            >
                {busy ? (
                    <Loader2 size={11} className="animate-spin" />
                ) : err ? (
                    <AlertTriangle size={11} />
                ) : status.last_sync_at ? (
                    <CheckCircle2 size={11} />
                ) : (
                    <RefreshCw size={11} />
                )}
                {label}
                <ChevronDown
                    size={11}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-[380px] border border-virtus-orange/40 bg-[#0A0A0A] shadow-2xl shadow-black z-50"
                    data-testid="sync-history-panel"
                >
                    <div className="px-4 py-3 border-b border-virtus-orange/20 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <History size={12} className="text-virtus-orange" />
                            <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-virtus-orange">
                                Recent Syncs
                            </span>
                        </div>
                        {isAdmin && (
                            <button
                                onClick={runNow}
                                disabled={busy}
                                className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.24em] uppercase text-white px-2 py-1 border border-virtus-orange/60 bg-virtus-orange/10 hover:bg-virtus-orange/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="sync-run-now-button"
                            >
                                {busy ? (
                                    <Loader2 size={10} className="animate-spin" />
                                ) : (
                                    <Play size={10} />
                                )}
                                Run now
                            </button>
                        )}
                    </div>

                    <div className="px-4 py-3 border-b border-virtus-orange/10">
                        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-gray-500">
                            Last Synced
                        </div>
                        <div
                            className="mt-1 font-mono text-[12px] text-white tabular-nums"
                            data-testid="sync-last-synced-stamp"
                        >
                            {formatFullStamp(status.last_sync_at) || "—"}
                        </div>
                        <div className="mt-0.5 font-mono text-[9px] tracking-[0.2em] uppercase text-gray-500">
                            {status.board_name ? `${status.board_name} · ` : ""}
                            Polls every {status.interval_minutes || 5} min
                        </div>
                    </div>

                    <div className="max-h-[280px] overflow-y-auto" data-testid="sync-history-list">
                        {history.length === 0 ? (
                            <div className="px-4 py-6 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-gray-500">
                                No sync runs recorded yet.
                            </div>
                        ) : (
                            history.map((run, idx) => (
                                <HistoryRow key={run.id || idx} run={run} />
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function HistoryRow({ run }) {
    const ok = run.status === "ok";
    const counts = run.counts || {};
    const total = (counts.updated_items || 0) + (counts.inserted_items || 0) + (counts.deleted_items || 0);
    return (
        <div
            className="px-4 py-2.5 border-b border-virtus-orange/5 hover:bg-virtus-orange/5 flex items-start gap-3"
            data-testid="sync-history-row"
        >
            <div className="mt-0.5">
                {ok ? (
                    <CheckCircle2 size={12} className="text-virtus-orange" />
                ) : (
                    <AlertTriangle size={12} className="text-virtus-stuck" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono text-[11px] text-white tabular-nums">
                        {formatFullStamp(run.ran_at) || "—"}
                    </span>
                    <span
                        className={`font-mono text-[9px] tracking-[0.2em] uppercase ${
                            ok ? "text-virtus-orange" : "text-virtus-stuck"
                        }`}
                    >
                        {ok ? "OK" : "ERROR"}
                    </span>
                </div>
                {ok ? (
                    <div className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-gray-400">
                        {total === 0 ? (
                            <span>No changes</span>
                        ) : (
                            <>
                                {counts.updated_items ? (
                                    <span className="mr-2">
                                        <span className="text-white">{counts.updated_items}</span> updated
                                    </span>
                                ) : null}
                                {counts.inserted_items ? (
                                    <span className="mr-2">
                                        <span className="text-white">{counts.inserted_items}</span> added
                                    </span>
                                ) : null}
                                {counts.deleted_items ? (
                                    <span className="mr-2">
                                        <span className="text-white">{counts.deleted_items}</span> deleted
                                    </span>
                                ) : null}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="mt-1 font-mono text-[9px] text-virtus-stuck truncate" title={run.message}>
                        {run.message || "Unknown error"}
                    </div>
                )}
            </div>
        </div>
    );
}

function ageLabel(iso, _tick) {
    if (!iso) return null;
    try {
        const t = new Date(iso).getTime();
        const now = Date.now();
        const s = Math.max(0, Math.floor((now - t) / 1000));
        if (s < 60) return `${s}s ago`;
        const m = Math.floor(s / 60);
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        return `${h}h ago`;
    } catch (_e) {
        return null;
    }
}

function formatFullStamp(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return null;
        // e.g. "12 FEB 2026 · 14:32:05 Z / 09:32 EST"
        const datePart = d
            .toLocaleDateString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                timeZone: "UTC",
            })
            .toUpperCase()
            .replace(/,/g, "");
        const zulu = d
            .toLocaleTimeString("en-US", {
                hour12: false,
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZone: "UTC",
            });
        const est = d
            .toLocaleTimeString("en-US", {
                hour12: false,
                hour: "2-digit",
                minute: "2-digit",
                timeZone: "America/New_York",
            });
        return `${datePart} · ${zulu} Z / ${est} EST`;
    } catch (_e) {
        return null;
    }
}
